// AGC: WeatherDashboard - Weather overview with current conditions from Open-Meteo API
// AGC: Visual Refresh Design System - Clean, data-centric layout
// AGC: Supports farm location selection for multi-farm users
import React, { useState, useMemo, useEffect } from 'react'
import {
  Wind,
  Droplets,
  Sun,
  CloudRain,
  ChevronRight,
  RefreshCw,
  MapPin,
  Radio,
  Truck,
  Sparkles,
  ArrowRight,
  ChevronDown,
  Cloud,
  CloudSun,
  CloudSnow,
  CloudLightning,
  Loader2,
  X,
} from 'lucide-react'
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { Card, BottomNav, Badge } from '../shared'
import { formatTemperature, formatPercentage, formatWindSpeed } from '@droidfarm/shared'
import { useWeather } from '../../hooks/useWeather'
import { useFarms } from '../../context/FarmContext'
import { useLotes } from '../../context/LotesContext'
import { WEATHER_CONDITIONS, type Screen, type WeatherCondition, type Plot } from '../../types'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { track } from '../../lib/analytics'

interface WeatherDashboardProps {
  onNavigate: (screen: Screen) => void
}

// AGC: Map weather conditions to Lucide icons (smaller for header, white for header only)
const weatherIcons: Record<WeatherCondition, React.ReactNode> = {
  sunny: <Sun className="w-5 h-5 text-white" />,
  cloudy: <Cloud className="w-5 h-5 text-white" />,
  'partly-cloudy': <CloudSun className="w-5 h-5 text-white" />,
  rainy: <CloudRain className="w-5 h-5 text-white" />,
  stormy: <CloudLightning className="w-5 h-5 text-white" />,
  snowy: <CloudSnow className="w-5 h-5 text-white" />,
  foggy: <Cloud className="w-5 h-5 text-white" />,
  windy: <Wind className="w-5 h-5 text-white" />,
}

// AGC: Map weather conditions to colored icons (for hourly forecast)
const getHourlyWeatherIcon = (condition: WeatherCondition): React.ReactNode => {
  const icons: Record<WeatherCondition, React.ReactNode> = {
    sunny: <Sun className="w-5 h-5 text-amber-500" />,
    cloudy: <Cloud className="w-5 h-5 text-slate-500" />,
    'partly-cloudy': <CloudSun className="w-5 h-5 text-amber-400" />,
    rainy: <CloudRain className="w-5 h-5 text-blue-500" />,
    stormy: <CloudLightning className="w-5 h-5 text-purple-600" />,
    snowy: <CloudSnow className="w-5 h-5 text-cyan-400" />,
    foggy: <Cloud className="w-5 h-5 text-gray-400" />,
    windy: <Wind className="w-5 h-5 text-sky-500" />,
  }
  return icons[condition] || icons.cloudy
}

/**
 * AGC: WeatherDashboard - Main weather overview screen
 * AGC: Visual Refresh design:
 *   - Muted accent gradient header
 *   - Clean card-based layout
 *   - Monochrome Lucide icons
 *   - Proper dark mode support
 */
export const WeatherDashboard: React.FC<WeatherDashboardProps> = ({ onNavigate }) => {
  const { t } = useTranslation()
  const { farms, currentFarm, setCurrentFarmById } = useFarms()
  const { plots } = useLotes()

  const { user } = useAuth()

  const [showLocationSelector, setShowLocationSelector] = useState(false)
  // AGC: Track selected lot for weather — persisted in localStorage to survive navigation
  const [selectedLotId, setSelectedLotId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem('droidfarm.weather.selectedLotId') || null
  })

  const setSelectedLotIdPersisted = (id: string | null) => {
    setSelectedLotId(id)
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem('droidfarm.weather.selectedLotId', id)
      else window.localStorage.removeItem('droidfarm.weather.selectedLotId')
    }
  }

  // AGC: Waitlist modal state
  const [waitlistProduct, setWaitlistProduct] = useState<'farmproweather' | 'sprayguard' | null>(null)
  const [waitlistEmail, setWaitlistEmail] = useState('')
  const [waitlistLoading, setWaitlistLoading] = useState(false)
  const [waitlistMessage, setWaitlistMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const openWaitlistModal = (product: 'farmproweather' | 'sprayguard') => {
    setWaitlistProduct(product)
    setWaitlistEmail(user?.email || '')
    setWaitlistMessage(null)
  }

  const closeWaitlistModal = () => {
    setWaitlistProduct(null)
    setWaitlistEmail('')
    setWaitlistMessage(null)
  }

  const handleWaitlistSubmit = async () => {
    if (!waitlistProduct || !waitlistEmail.trim() || !user) return
    setWaitlistLoading(true)
    try {
      const q = query(
        collection(db, 'waitlist'),
        where('userId', '==', user.uid),
        where('product', '==', waitlistProduct)
      )
      const existing = await getDocs(q)
      if (!existing.empty) {
        setWaitlistMessage({ type: 'error', text: t('weather.alreadyRegistered') })
        setWaitlistLoading(false)
        return
      }
      await addDoc(collection(db, 'waitlist'), {
        product: waitlistProduct,
        email: waitlistEmail.trim(),
        userId: user.uid,
        userName: user.displayName || '',
        createdAt: serverTimestamp(),
      })
      setWaitlistMessage({ type: 'success', text: t('weather.registeredSuccess') })
      setTimeout(() => closeWaitlistModal(), 2000)
    } catch {
      setWaitlistMessage({ type: 'error', text: 'Error' })
    } finally {
      setWaitlistLoading(false)
    }
  }

  // AGC: Get lots for the current farm
  const currentFarmLots = useMemo(() => {
    if (!currentFarm) return []
    return plots.filter(p => p.fieldId === currentFarm.id || p.farmId === currentFarm.id)
  }, [plots, currentFarm])

  // AGC: Compute polygon center from lot coordinates
  const getLotCenter = (lot: Plot): { lat: number; lon: number } | undefined => {
    if (!lot.coordinates || lot.coordinates.length === 0) return undefined
    const lat = lot.coordinates.reduce((sum, c) => sum + c[0], 0) / lot.coordinates.length
    const lon = lot.coordinates.reduce((sum, c) => sum + c[1], 0) / lot.coordinates.length
    if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) return { lat, lon }
    return undefined
  }

  // AGC: Selected lot object
  const selectedLot = useMemo(() => {
    if (!selectedLotId) return null
    return currentFarmLots.find(l => l.id === selectedLotId) || null
  }, [selectedLotId, currentFarmLots])

  // AGC: Get coordinates from selected lot or farm
  const farmCoordinates = useMemo(() => {
    // AGC: If a lot is selected, use its polygon center
    if (selectedLot) {
      const center = getLotCenter(selectedLot)
      if (center) return center
    }
    // AGC: Otherwise use farm center
    if (currentFarm?.coordinates && Array.isArray(currentFarm.coordinates)) {
      const [lat, lng] = currentFarm.coordinates
      if (lat && lng && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lon: lng }
      }
    }
    return undefined
  }, [currentFarm, selectedLot])
  
  const { 
    current, 
    hourly, 
    sprayConditions, 
    location,
    isLoading,
    error,
    lastUpdated,
    tier,
    forecastDays,
    locationSource,
    refetch,
  } = useWeather({
    refreshInterval: 30 * 60 * 1000,
    coordinates: farmCoordinates,
  })

  // AGC: Display name for location selector (after useWeather so `location` is available)
  const locationDisplayName = useMemo(() => {
    if (selectedLot) return selectedLot.name
    return currentFarm?.name || location || t('weather.locationUnknown')
  }, [selectedLot, currentFarm, location, t])

  // AGC: Clear selected lot when farm changes
  useEffect(() => {
    setSelectedLotId(null)
  }, [currentFarm?.id])

  // AGC: Telemetry — fire once when weather data first arrives for this mount
  const weatherTrackedRef = React.useRef(false)
  useEffect(() => {
    if (current && !weatherTrackedRef.current) {
      weatherTrackedRef.current = true
      void track('feature_used', { feature_name: 'weather_check' })
    }
  }, [current])

  // AGC: Spray suitability colors
  const sprayColors = {
    favorable: { 
      bg: 'bg-emerald-500', 
      text: 'text-emerald-600 dark:text-emerald-400', 
      label: t('weather.sprayFavorable') || 'Favorable', 
      bgLight: 'bg-emerald-100 dark:bg-emerald-900/30' 
    },
    moderate: { 
      bg: 'bg-amber-500', 
      text: 'text-amber-600 dark:text-amber-400', 
      label: t('weather.sprayModerate') || 'Moderate', 
      bgLight: 'bg-amber-100 dark:bg-amber-900/30' 
    },
    unfavorable: { 
      bg: 'bg-red-500', 
      text: 'text-red-600 dark:text-red-400', 
      label: t('weather.sprayUnfavorable') || 'Unfavorable', 
      bgLight: 'bg-red-100 dark:bg-red-900/30' 
    },
  }

  const sprayStatus = sprayConditions ? sprayColors[sprayConditions.overall] : sprayColors.moderate

  // AGC: Loading state
  if (isLoading && !current) {
    return (
      <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-navy-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">{t('weather.loading') || 'Loading weather data...'}</p>
        </div>
      </div>
    )
  }

  // AGC: Error state
  if (error && !current) {
    return (
      <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="p-6 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 max-w-md text-center shadow-lg">
          <CloudRain className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">
            {t('weather.errorTitle') || 'Error loading data'}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={() => refetch(true)}
            className="px-4 py-2 bg-navy-700 text-white rounded-full hover:bg-navy-600 transition-colors font-medium"
          >
            {t('weather.retry') || 'Try again'}
          </button>
        </div>
      </div>
    )
  }

  const displayCurrent = current || {
    temperature: 0,
    condition: 'cloudy' as WeatherCondition,
    humidity: 0,
    windSpeed: 0,
    windDirection: 'N',
    uvIndex: 0,
    precipitation: 0,
    feelsLike: 0,
  }

  const deltaT = sprayConditions?.deltaT?.value ?? displayCurrent.deltaT ?? 5
  
  const getDeltaTColor = (value: number) => {
    if (value >= 2 && value <= 8) return { bg: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'Ideal' }
    if ((value > 8 && value <= 10) || (value >= 1 && value < 2)) return { bg: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: 'Marginal' }
    return { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: 'Inadequate' }
  }
  const deltaTColor = getDeltaTColor(deltaT)

  const getWeatherIcon = (condition: WeatherCondition) => {
    return weatherIcons[condition] || weatherIcons.cloudy
  }

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900 pb-20 transition-colors">
      {/* AGC: Header with current weather - Solid green gradient */}
      <header 
        className="text-white px-4 pt-4 pb-4 rounded-b-2xl relative sticky top-0 z-10"
        style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
      >
        {/* AGC: Decorative background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          />
          <div 
            className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
          />
        </div>
        
        {/* AGC: Weather display - Compact single row layout */}
        <div className="flex items-center justify-between relative z-10">
          {/* AGC: Left side - Weather icon in rounded container + temperature */}
          <div className="flex items-center gap-3">
            {/* AGC: Weather icon in rounded container - matches Lots/Tasks style */}
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              {getWeatherIcon(displayCurrent.condition)}
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold tracking-tight">{displayCurrent.temperature}</span>
                <span className="text-lg">°C</span>
              </div>
              <p className="text-white/80 text-xs">
                {WEATHER_CONDITIONS.find(c => c.id === displayCurrent.condition)?.name || displayCurrent.condition}
              </p>
            </div>
          </div>
          
          {/* AGC: Right side - Wind, Humidity, Refresh button inline */}
          <div className="flex items-center gap-3">
            <div className="text-center">
              <Wind className="w-4 h-4 mx-auto text-white/70 mb-0.5" />
              <span className="text-white/90 text-xs">{displayCurrent.windSpeed} km/h</span>
            </div>
            <div className="text-center">
              <Droplets className="w-4 h-4 mx-auto text-white/70 mb-0.5" />
              <span className="text-white/90 text-xs">{displayCurrent.humidity}%</span>
            </div>
            {/* AGC: Refresh button inline with wind/humidity */}
            <button
              onClick={() => refetch(true)}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={t('weather.refresh') || 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* AGC: Location selector - bigger, bold, tappable with dropdown arrow */}
        <div className="flex items-center justify-between mt-2 relative z-10">
          <div className="relative">
            <button
              onClick={() => setShowLocationSelector(!showLocationSelector)}
              className="flex items-center gap-1.5 hover:bg-white/10 rounded-lg px-2 py-1 -ml-2 transition-colors"
            >
              <MapPin className="w-4 h-4 text-white/80" />
              <span className="text-white/60 text-sm">{t('weather.place')}:</span>
              <span className="text-white font-bold text-sm max-w-[180px] truncate">{locationDisplayName}</span>
              <ChevronDown className={`w-4 h-4 text-white/80 transition-transform ${showLocationSelector ? 'rotate-180' : ''}`} />
            </button>

            {/* AGC: Hierarchical location dropdown - farms then lots */}
            {showLocationSelector && (
              <Card variant="elevated" padding="none" className="absolute top-full left-0 mt-1 z-50 min-w-[220px] max-h-[300px] overflow-y-auto">
                {/* AGC: Farms section */}
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  {t('weather.farmsSection')}
                </div>
                {farms.map(farm => (
                  <button
                    key={farm.id}
                    onClick={() => {
                      setCurrentFarmById(farm.id)
                      setSelectedLotId(null)
                      setShowLocationSelector(false)
                    }}
                    className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors ${
                      farm.id === currentFarm?.id && !selectedLotId
                        ? 'bg-accent-50 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
                        : 'text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{farm.name}</span>
                  </button>
                ))}

                {/* AGC: Lots section - show lots for the current farm */}
                {currentFarmLots.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider bg-slate-50 dark:bg-slate-800 border-b border-t border-slate-200 dark:border-slate-700">
                      {t('weather.lotsSection')}
                    </div>
                    {currentFarmLots.map(lot => (
                      <button
                        key={lot.id}
                        onClick={() => {
                          setSelectedLotId(lot.id)
                          setShowLocationSelector(false)
                        }}
                        className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors ${
                          selectedLotId === lot.id
                            ? 'bg-accent-50 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
                            : 'text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="w-4 h-4 flex-shrink-0 rounded bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-sm bg-emerald-500" />
                        </div>
                        <span className="truncate">{lot.name}</span>
                      </button>
                    ))}
                  </>
                )}
              </Card>
            )}
          </div>

          {/* AGC: Last updated timestamp */}
          {lastUpdated && (
            <span className="text-white/40 text-xs">
              {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </header>

      {/* AGC: Spray Suitability Card */}
      {sprayConditions && (
        <div className="px-4 mt-4">
          <Card 
            variant="elevated" 
            padding="md"
            onClick={() => onNavigate('spray-suitability', { weatherCoordinates: farmCoordinates })}
            hover
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-12 h-12 rounded-xl ${sprayStatus.bgLight} flex items-center justify-center`}>
                  <div className={`w-5 h-5 rounded-full ${sprayStatus.bg}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('weather.spray') || 'Spraying'}:</p>
                    <p className={`font-semibold ${sprayStatus.text}`}>{sprayStatus.label}</p>
                  </div>
                  {sprayConditions.bestWindow && (
                    <p className="text-sm text-accent-600 dark:text-accent-400 font-medium mt-0.5">
                      {t('weather.bestWindow') || 'Best window'}: {sprayConditions.bestWindow.start} - {sprayConditions.bestWindow.end}
                    </p>
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
            </div>
          </Card>
        </div>
      )}

      {/* AGC: Delta-T Card */}
      <div className="px-4 mt-3">
        <Card 
          variant="default" 
          padding="md"
          onClick={() => onNavigate('spray-suitability', { weatherCoordinates: farmCoordinates })}
          hover
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                <span className="text-sm font-bold text-sky-600 dark:text-sky-400">ΔT</span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-50">Delta-T</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('weather.deltaTDesc') || 'Evaporation index'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${deltaTColor.text}`}>{deltaT.toFixed(1)}°C</p>
              <p className={`text-xs ${deltaTColor.text}`}>{deltaTColor.label}</p>
            </div>
          </div>

          {/* AGC: Delta-T Visual Scale - Colored by ranges */}
          <div className="relative h-6 rounded-full overflow-hidden flex">
            {/* AGC: Red zone: 0-2 (Inadequate) */}
            <div className="bg-red-500" style={{ width: `${(2 / 14) * 100}%` }} />
            {/* AGC: Green zone: 2-8 (Ideal) */}
            <div className="bg-emerald-500" style={{ width: `${(6 / 14) * 100}%` }} />
            {/* AGC: Yellow zone: 8-10 (Marginal) */}
            <div className="bg-amber-500" style={{ width: `${(2 / 14) * 100}%` }} />
            {/* AGC: Red zone: 10-14+ (Inadequate) */}
            <div className="bg-red-500 flex-1" />
            
            {/* AGC: Indicator marker */}
            <div 
              className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg rounded-full transform -translate-x-1/2"
              style={{ left: `${Math.min(Math.max((deltaT / 14) * 100, 0), 100)}%` }}
            >
              <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 w-2.5 h-2.5 bg-white rounded-full border-2 border-slate-800" />
            </div>
          </div>

          <div className="flex justify-between mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span>0</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">2-8 ideal</span>
            <span>14+</span>
          </div>

          <div className="flex items-center justify-center gap-1 mt-3 text-xs text-slate-400">
            <span>{t('weather.tapForDetails') || 'Tap for details'}</span>
            <ChevronRight className="w-3 h-3" />
          </div>
        </Card>
      </div>

      {/* AGC: Weather Metrics Grid */}
      <div className="px-4 mt-4">
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            icon={<Wind className="w-5 h-5 text-sky-500" />}
            label={t('weather.wind') || 'Wind'}
            value={formatWindSpeed(displayCurrent.windSpeed)}
            subtitle={displayCurrent.windDirection}
          />
          <MetricCard
            icon={<Droplets className="w-5 h-5 text-cyan-500" />}
            label={t('weather.humidity') || 'Humidity'}
            value={formatPercentage(displayCurrent.humidity)}
          />
          <MetricCard
            icon={<Sun className="w-5 h-5 text-amber-500" />}
            label={t('weather.uvIndex') || 'UV Index'}
            value={displayCurrent.uvIndex.toString()}
            subtitle={displayCurrent.uvIndex >= 8 ? 'Very High' : displayCurrent.uvIndex >= 6 ? 'High' : 'Moderate'}
          />
          <MetricCard
            icon={<CloudRain className="w-5 h-5 text-slate-500" />}
            label={t('weather.precipitation') || 'Precipitation'}
            value={`${displayCurrent.precipitation} mm`}
          />
        </div>
      </div>

      {/* AGC: Hourly Forecast */}
      {hourly.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between px-4 mb-3">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              {t('weather.nextHours') || 'Next Hours'}
            </h3>
          </div>
          
          <div 
            className="flex gap-2 overflow-x-auto px-4 pb-2 no-scrollbar"
          >
            {hourly.slice(0, 12).map((hour, index) => (
              <Card
                key={`${hour.hour}-${index}`}
                variant="default"
                padding="sm"
                className="flex-shrink-0 w-[70px]"
              >
                <div className="flex flex-col items-center">
                  <span className="text-xs text-slate-500 dark:text-slate-400">{hour.hour}</span>
                  <div className="my-2">{getHourlyWeatherIcon(hour.condition)}</div>
                  <span className="font-semibold text-slate-900 dark:text-slate-50">{hour.temperature}°</span>
                  {hour.precipitationProbability > 20 && (
                    <span className="text-xs text-sky-500 mt-0.5">
                      {hour.precipitationProbability}%
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* AGC: Forecast Link */}
      <div className="px-4 mt-6">
        <Card 
          onClick={() => onNavigate('forecast')} 
          hover
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-slate-900 dark:text-slate-50">
                {t('weather.forecastDays', { days: forecastDays }) || `${forecastDays} Day Forecast`}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {tier === 'free' 
                  ? (t('weather.upgradeTo14Days') || 'Upgrade for 14 days') 
                  : (t('weather.viewForecast') || 'View detailed forecast')
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              {tier === 'free' && (
                <Badge variant="warning" size="sm">{t('weather.free7Days') || '7 days'}</Badge>
              )}
              {(tier === 'pro' || tier === 'premium') && (
                <Badge variant="success" size="sm">{t('weather.pro14Days') || '14 days'}</Badge>
              )}
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* AGC: Promotional Panels */}
      <div className="px-4 mt-6 space-y-4">
        {/* AGC: FarmProWeather Station Panel */}
        <div 
          className="
            relative overflow-hidden rounded-xl
            bg-gradient-to-br from-sky-500 via-sky-600 to-cyan-600
            p-5 text-white shadow-elevated
            cursor-pointer transition-transform duration-200
            hover:scale-[1.01] active:scale-[0.99]
          "
          onClick={() => openWaitlistModal('farmproweather')}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12" />

          <div className="relative z-10">
            <div className="absolute top-0 right-0 bg-white/25 backdrop-blur-sm rounded-bl-lg rounded-tr-xl px-3 py-1">
              <span className="text-xs font-bold text-white uppercase tracking-wide">{t('weather.comingSoon')}</span>
            </div>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Radio className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-lg font-bold">FarmProWeather</h3>
                    <Sparkles className="w-4 h-4 text-amber-300" />
                  </div>
                  <p className="text-sm text-sky-100">{t('weather.weatherStation') || 'Weather Station'}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white/80" />
            </div>
            
            <p className="text-sm text-sky-50 mb-3 leading-relaxed">
              {t('weather.weatherStationDesc') || 'Exclusive weather data from your farm. Precise forecasts based on real field conditions.'}
            </p>
            
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                <span className="text-sky-50">{t('weather.realTimeData') || 'Real-time data'}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
                <span className="text-sky-50">{t('weather.exclusiveForecast') || 'Exclusive forecast'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* AGC: SprayGuard Panel */}
        <div 
          className="
            relative overflow-hidden rounded-xl
            bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600
            p-5 text-white shadow-elevated
            cursor-pointer transition-transform duration-200
            hover:scale-[1.01] active:scale-[0.99]
          "
          onClick={() => openWaitlistModal('sprayguard')}
        >
          <div className="absolute top-0 left-0 w-28 h-28 bg-white/10 rounded-full -ml-14 -mt-14" />
          <div className="absolute bottom-0 right-0 w-36 h-36 bg-white/5 rounded-full -mr-18 -mb-18" />

          <div className="relative z-10">
            <div className="absolute top-0 right-0 bg-white/25 backdrop-blur-sm rounded-bl-lg rounded-tr-xl px-3 py-1">
              <span className="text-xs font-bold text-white uppercase tracking-wide">{t('weather.comingSoon')}</span>
            </div>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-lg font-bold">SprayGuard</h3>
                    <Sparkles className="w-4 h-4 text-amber-300" />
                  </div>
                  <p className="text-sm text-emerald-100">{t('weather.sprayerMonitor') || 'Sprayer Monitor'}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white/80" />
            </div>
            
            <p className="text-sm text-emerald-50 mb-3 leading-relaxed">
              {t('weather.sprayerMonitorDesc') || 'Real-time sprayer data. Monitor ideal spraying conditions directly from your vehicle.'}
            </p>
            
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
                <span className="text-emerald-50">{t('weather.vehicleRealtime') || 'Vehicle real-time'}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-300" />
                <span className="text-emerald-50">{t('weather.sprayOptimization') || 'Spray optimization'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AGC: Open-Meteo Attribution */}
      <div className="px-4 mt-6 mb-4 text-center">
        <a 
          href="https://open-meteo.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          {t('weather.attribution') || 'Weather data by Open-Meteo.com'}
        </a>
      </div>

      {/* AGC: Waitlist Coming Soon modal */}
      {waitlistProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-sm p-6 relative shadow-xl">
            <button
              onClick={closeWaitlistModal}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                waitlistProduct === 'farmproweather'
                  ? 'bg-sky-100 dark:bg-sky-900/30'
                  : 'bg-emerald-100 dark:bg-emerald-900/30'
              }`}>
                {waitlistProduct === 'farmproweather'
                  ? <Radio className="w-8 h-8 text-sky-500" />
                  : <Truck className="w-8 h-8 text-emerald-500" />
                }
              </div>

              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-1">
                {waitlistProduct === 'farmproweather' ? 'FarmProWeather' : 'SprayGuard'}
              </h2>
              <span className="inline-block bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-3">
                {t('weather.comingSoon')}
              </span>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                {t('weather.notifyMeDesc')}
              </p>

              {waitlistMessage && (
                <div className={`w-full p-3 rounded-lg mb-4 text-sm ${
                  waitlistMessage.type === 'success'
                    ? 'bg-green-50 dark:bg-emerald-900/30 text-green-700 dark:text-emerald-400 border border-green-200 dark:border-emerald-800'
                    : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                }`}>
                  {waitlistMessage.text}
                </div>
              )}

              <input
                type="email"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder={t('weather.yourEmail')}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-50 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />

              <button
                onClick={handleWaitlistSubmit}
                disabled={waitlistLoading || !waitlistEmail.trim()}
                className={`w-full py-3 rounded-xl font-semibold text-white transition-colors ${
                  waitlistProduct === 'farmproweather'
                    ? 'bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300'
                    : 'bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300'
                }`}
              >
                {waitlistLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  t('weather.notifyMe')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav
        active="weather"
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard')
          else if (tab === 'fields') onNavigate('fields')
          else if (tab === 'tasks') onNavigate('tasks')
        }}
      />
    </div>
  )
}

/**
 * AGC: Metric Card Component with Visual Refresh design
 */
interface MetricCardProps {
  icon: React.ReactNode
  label: string
  value: string
  subtitle?: string
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, label, value, subtitle }) => {
  return (
    <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <p className="text-xl font-bold text-slate-900 dark:text-slate-50">{value}</p>
      {subtitle && (
        <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
      )}
    </div>
  )
}

export default WeatherDashboard
